import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
} from '@mui/material';
import {
  School,
  EmojiEvents,
  HealthAndSafety,
  SelfImprovement,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const About: React.FC = () => {
  const navigate = useNavigate();
  const team = [
    {
      name: 'Dr. Ron Pancho',
      title: 'Principal Chiropractor',
      qualifications: [
        'Bachelor of Health Science',
        'Master of Clinical Chiropractic',
        'Sports Chiropractic Australia (SCA)',
        'Australian Strength and Conditioning Association (ASCA)',
        'Sports Medicine Australia (SMA)',
        'International Federation of Sports Chiropractic (FICS)',
      ],
      specialties: [
        'Sports & Exercise Rehabilitation',
        'Spinal Manipulation',
        'Exercise Prescription',
        'Workplace Injuries',
      ],
      description: 'Dr. Ron holds a Bachelor of Health Science and a Master of Clinical Chiropractic, along with memberships with Sports Chiropractic Australia (SCA), the Australian Strength and Conditioning Association (ASCA), Sports Medicine Australia (SMA), and the International Federation of Sports Chiropractic (FICS). With a deep passion for sports and exercise rehabilitation, he has had the opportunity to apply this expertise across various sporting clubs and teams, working with athletes at all levels. His experience extends to the "athletes of the workforce," where he has spent several years in workplace health, helping individuals maintain optimal performance and well-being. Dr. Ron is enthusiastic about sharing his knowledge and experience with others who are passionate about health and sports performance.',
      avatar: '/staff/ron.png',
    },
    {
      name: 'Kylie-Faye James',
      title: 'Myotherapist & Remedial Massage Therapist',
      qualifications: [
        'Advanced Diploma of Myotherapy',
        'Diploma of Remedial Massage',
      ],
      specialties: [
        'Myotherapy',
        'Deep Tissue Massage',
        'Myofascial Release',
        'Trigger Point Therapy',
      ],
      description: 'Kylie-Faye is a highly skilled Myotherapist and Remedial Massage Therapist who specializes in comprehensive musculoskeletal assessment and treatment. She combines advanced myotherapy techniques with remedial massage to provide effective pain relief and functional improvement.',
      avatar: '/staff/kylie.jpeg',
    },
    {
      name: 'Dr. Lawrence Staub',
      title: 'Associate Chiropractor',
      qualifications: [
        'Bachelor of Clinical Science',
        'Bachelor of Chiropractic Science',
      ],
      specialties: [
        'Musculoskeletal Care',
        'Lifestyle Guidance',
        'Prevention',
        'Community Health',
      ],
      description: 'Health By Chiropractic warmly welcomes, Dr. Lawrence who is returning to Melton to serve our community in the growing suburbs of Melbourne\'s west, delivering exceptional, patient-centred care that supports overall wellness. With over 20 years of clinical experience, he emphasises proactive prevention, lifestyle guidance, and evidence-based chiropractic care to enhance daily living. A proud RMIT graduate (2003, Distinction) and founder/director of the South Pacific Allied Health charity, his non-profit work reflects his commitment to health education and community empowerment. In his spare time, he enjoys beekeeping.',
      avatar: '/staff/IMG5722.jpeg',
    },
  ];

  const clinicValues = [
    {
      icon: <HealthAndSafety />,
      title: 'Evidence-Based Care',
      description: 'All treatments grounded in the latest research and proven clinical outcomes.',
    },
    {
      icon: <SelfImprovement />,
      title: 'Holistic Approach',
      description: 'Treating the whole person, not just symptoms, for lasting wellness.',
    },
    {
      icon: <EmojiEvents />,
      title: 'Continuous Learning',
      description: 'Our practitioners stay current with ongoing education and training.',
    },
    {
      icon: <School />,
      title: 'Patient Education',
      description: 'Empowering patients with knowledge for better health outcomes.',
    },
  ];

  return (
    <Box
      id="about"
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: 'white',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <MotionBox
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}
        >
          <Typography
            variant="h2"
            sx={{
              mb: { xs: 2, md: 2 },
              background: 'linear-gradient(45deg, #2c3e50, #8bc34a)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
              px: { xs: 2, md: 0 },
            }}
          >
            Meet Our Expert Team
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
              mb: { xs: 4, md: 6 },
              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
              px: { xs: 3, md: 0 },
              lineHeight: { xs: 1.6, md: 1.6 },
            }}
          >
            Our dedicated team of healthcare professionals is committed to providing
            exceptional care using the latest evidence-based techniques.
          </Typography>
        </MotionBox>

        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mb: { xs: 8, md: 10 } }}>
          {team.map((member, index) => (
            <Grid item xs={12} md={4} key={index}>
              <MotionCard
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                sx={{
                  height: '100%',
                  borderRadius: { xs: '12px', md: '16px' },
                  border: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  mx: { xs: 1, md: 0 },
                  '&:hover': {
                    transform: { xs: 'none', md: 'translateY(-8px)' },
                    boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.1)', md: '0 8px 30px rgba(0,0,0,0.15)' },
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: { xs: 80, md: 100 },
                      height: { xs: 80, md: 100 },
                      mx: 'auto',
                      mb: { xs: 2.5, md: 3 },
                      border: '3px solid #8bc34a',
                      '& img': {
                        objectPosition: member.name === 'Kylie-Faye James' ? 'center 30%' : 'center center',
                      },
                    }}
                    src={member.avatar}
                    alt={member.name}
                  />

                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: { xs: 0.5, md: 1 },
                      fontSize: { xs: '1.3rem', md: '1.5rem' },
                    }}
                  >
                    {member.name}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{ 
                      color: 'primary.main', 
                      fontWeight: 500, 
                      mb: { xs: 2.5, md: 3 },
                      fontSize: { xs: '1rem', md: '1.1rem' },
                    }}
                  >
                    {member.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ 
                      color: 'text.secondary', 
                      mb: { xs: 2.5, md: 3 }, 
                      lineHeight: 1.6,
                      fontSize: { xs: '0.875rem', md: '0.875rem' },
                      px: { xs: 1, md: 0 },
                    }}
                  >
                    {member.description}
                  </Typography>

                  <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        mb: { xs: 0.5, md: 1 }, 
                        fontWeight: 600,
                        fontSize: { xs: '0.95rem', md: '1rem' },
                      }}
                    >
                      Qualifications:
                    </Typography>
                    {member.qualifications.map((qual, qualIndex) => (
                      <Typography
                        key={qualIndex}
                        variant="body2"
                        sx={{ 
                          color: 'text.secondary', 
                          fontSize: { xs: '0.8rem', md: '0.9rem' },
                          px: { xs: 1, md: 0 },
                        }}
                      >
                        • {qual}
                      </Typography>
                    ))}
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: { xs: 0.5, md: 1 }, 
                    justifyContent: 'center',
                    px: { xs: 1, md: 0 },
                  }}>
                    {member.specialties.slice(0, 3).map((specialty, specIndex) => (
                      <Chip
                        key={specIndex}
                        label={specialty}
                        size="small"
                        sx={{
                          backgroundColor: '#e8f5e8',
                          color: '#2c3e50',
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', md: '0.75rem' },
                          height: { xs: '24px', md: '28px' },
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>

        <MotionBox
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          sx={{
            p: { xs: 3, md: 6 },
            backgroundColor: '#f8f9fa',
            borderRadius: { xs: '12px', md: '20px' },
            mb: { xs: 6, md: 8 },
            mx: { xs: 1, md: 0 },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              fontWeight: 600,
              color: 'primary.main',
              fontSize: { xs: '1.5rem', md: '2.125rem' },
              px: { xs: 1, md: 0 },
            }}
          >
            Our Clinic Values
          </Typography>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {clinicValues.map((value, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: 1.5, md: 2 },
                    px: { xs: 1, md: 0 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 40, md: 50 },
                      height: { xs: 40, md: 50 },
                      borderRadius: { xs: '10px', md: '12px' },
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.main',
                      flexShrink: 0,
                      '& svg': { fontSize: { xs: 20, md: 24 } },
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: { xs: 0.5, md: 1 },
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.875rem', md: '0.875rem' },
                        lineHeight: 1.5,
                      }}
                    >
                      {value.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </MotionBox>

        <MotionBox
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          sx={{ textAlign: 'center', px: { xs: 2, md: 0 } }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              mb: { xs: 2.5, md: 3 }, 
              fontWeight: 600,
              fontSize: { xs: '1.3rem', md: '1.5rem' },
              px: { xs: 1, md: 0 },
            }}
          >
            Ready to Start Your Journey to Better Health?
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(45deg, #8bc34a, #689f38)',
              px: { xs: 3, md: 4 },
              py: { xs: 1.2, md: 1.5 },
              fontSize: { xs: '1rem', md: '1.1rem' },
              minWidth: { xs: '250px', md: 'auto' },
              '&:hover': {
                background: 'linear-gradient(45deg, #689f38, #558b2f)',
                transform: 'scale(1.05)',
              },
              boxShadow: '0 4px 15px rgba(139, 195, 74, 0.3)',
            }}
            onClick={() => navigate('/bookings')}
          >
            Book Your Appointment Today
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default About;